import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Trash2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function WelcomeBanner({ onReset }) {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem("mag_welcome_seen") === "1");
    } catch (_e) { /* noop */ }
  }, []);

  if (dismissed || !user) return null;

  const dismiss = () => {
    try { localStorage.setItem("mag_welcome_seen", "1"); } catch (_e) { /* noop */ }
    setDismissed(true);
  };

  const reset = async () => {
    if (!window.confirm("Eliminare tutti i dati demo e partire da un magazzino vuoto? Questa azione non si può annullare.")) return;
    setResetting(true);
    try {
      // seed=false + conferma esplicita (backend richiede confirmation="SVUOTA")
      await axios.post(`${API}/demo/reset`, { confirmation: "SVUOTA", seed: false });
      toast.success("Dati demo rimossi. Magazzino vuoto e pronto per i tuoi prodotti.");
      dismiss();
      onReset?.();
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail || e.message });
    } finally {
      setResetting(false);
    }
  };

  const firstName = user.name?.split(" ")[0] || "";

  return (
    <div
      data-testid="welcome-banner"
      className="relative mag-card p-5 sm:p-6 bg-gradient-to-br from-[#E8F3EC] to-[#F4F1ED] border-[#C5E1D1]"
    >
      <button
        onClick={dismiss}
        data-testid="welcome-dismiss-btn"
        className="absolute top-3 right-3 h-7 w-7 rounded-md text-[#5C5F5A] hover:bg-white/60 flex items-center justify-center"
        title="Chiudi"
      >
        <X size={14} />
      </button>

      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-[#2E4A3B] text-white flex items-center justify-center flex-shrink-0">
          <Sparkles size={20} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-[#1A1C1A]" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Benvenuto{firstName ? `, ${firstName}` : ""}!
          </h3>
          <p className="text-sm text-[#5C5F5A] mt-1">
            Abbiamo caricato alcuni <strong>dati di esempio</strong> così puoi esplorare l&apos;app. Quando sei pronto, cancellali e carica i tuoi veri prodotti.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        <Link
          to="/app/prodotti"
          data-testid="welcome-action-scan"
          className="flex items-center gap-3 p-3 rounded-lg bg-white border border-[#EAE7E1] hover:border-[#2E4A3B] transition-all group"
          onClick={dismiss}
        >
          <div className="h-8 w-8 rounded-md bg-[#E8F3EC] text-[#2E4A3B] flex items-center justify-center">
            <Camera size={16} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium">Scansiona il primo scontrino</div>
            <div className="text-xs text-[#8E918C]">Carica prodotti dalla foto in 10 secondi</div>
          </div>
        </Link>
        <button
          onClick={reset}
          disabled={resetting}
          data-testid="welcome-action-reset"
          className="flex items-center gap-3 p-3 rounded-lg bg-white border border-[#EAE7E1] hover:border-[#C65D47] hover:bg-[#FCEAE8] transition-all text-left"
        >
          <div className="h-8 w-8 rounded-md bg-[#FCEAE8] text-[#C65D47] flex items-center justify-center">
            <Trash2 size={16} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium">{resetting ? "Rimozione..." : "Cancella dati demo"}</div>
            <div className="text-xs text-[#8E918C]">Svuota e inizia con il tuo magazzino vero</div>
          </div>
        </button>
      </div>
    </div>
  );
}
